import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";

import { cn } from "../lib/cn";

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export const MenuRadioGroup = DropdownMenu.RadioGroup;

export const MenuContent = forwardRef<
  ElementRef<typeof DropdownMenu.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Content>
>(function MenuContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-80 min-w-40 overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-popover",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          className,
        )}
        {...props}
      />
    </DropdownMenu.Portal>
  );
});

export interface MenuItemProps extends ComponentPropsWithoutRef<typeof DropdownMenu.Item> {
  tone?: "default" | "danger";
}

export const MenuItem = forwardRef<ElementRef<typeof DropdownMenu.Item>, MenuItemProps>(
  function MenuItem({ className, tone = "default", ...props }, ref) {
    return (
      <DropdownMenu.Item
        ref={ref}
        className={cn(
          "density-control-sm flex cursor-default select-none items-center gap-2 rounded-md px-2.5 text-sm outline-none transition-colors",
          "data-disabled:pointer-events-none data-disabled:opacity-50",
          tone === "danger"
            ? "text-status-danger focus:bg-status-danger/5"
            : "text-primary focus:bg-surface-elevated",
          className,
        )}
        {...props}
      />
    );
  },
);

export const MenuCheckboxItem = forwardRef<
  ElementRef<typeof DropdownMenu.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DropdownMenu.CheckboxItem> & {
    indicatorStyle?: "check" | "checkbox";
  }
>(function MenuCheckboxItem({ children, className, indicatorStyle = "check", ...props }, ref) {
  return (
    <DropdownMenu.CheckboxItem
      ref={ref}
      className={cn(
        "density-control-sm relative flex cursor-default select-none items-center gap-2 rounded-md pr-2.5 pl-8 text-sm text-primary outline-none transition-colors",
        "focus:bg-surface-elevated data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          "absolute left-2.5 flex h-4 w-4 items-center justify-center",
          indicatorStyle === "checkbox" && "rounded-sm border",
          indicatorStyle === "checkbox" &&
            (props.checked
              ? "border-focus bg-focus text-surface"
              : "border-control-border-hover bg-surface"),
        )}
      >
        <DropdownMenu.ItemIndicator>
          <Check
            className={cn(
              "h-3.5 w-3.5",
              indicatorStyle === "checkbox" ? "text-current" : "text-interaction-text",
            )}
            strokeWidth={2.5}
            aria-hidden
          />
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </DropdownMenu.CheckboxItem>
  );
});

export const MenuRadioItem = forwardRef<
  ElementRef<typeof DropdownMenu.RadioItem>,
  ComponentPropsWithoutRef<typeof DropdownMenu.RadioItem>
>(function MenuRadioItem({ children, className, ...props }, ref) {
  return (
    <DropdownMenu.RadioItem
      ref={ref}
      className={cn(
        "density-control-sm relative flex cursor-default select-none items-center gap-2 rounded-md pr-2.5 pl-8 text-sm text-primary outline-none transition-colors",
        "focus:bg-surface-elevated data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2.5 flex h-4 w-4 items-center justify-center">
        <DropdownMenu.ItemIndicator>
          <Check className="h-3.5 w-3.5 text-interaction-text" strokeWidth={2.5} aria-hidden />
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </DropdownMenu.RadioItem>
  );
});

export const MenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenu.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Separator>
>(function MenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenu.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-border/70", className)}
      {...props}
    />
  );
});

/** A non-interactive heading for a group of menu items. */
export const MenuLabel = forwardRef<
  ElementRef<typeof DropdownMenu.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Label>
>(function MenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenu.Label
      ref={ref}
      className={cn("px-2.5 pb-1 pt-2 text-xs font-medium text-muted", className)}
      {...props}
    />
  );
});
